import { Injectable } from '@angular/core';
import { PlantDto } from '../Models/plantDto.interface';
import { GrpcMetadata } from '@ngx-grpc/common';
import { map, Observable, of } from 'rxjs';
import { Empty } from '@ngx-grpc/well-known-types';
import { AuthService } from '../../../authentication/service/auth.service';
import { CustomerAssetsEndpointClient } from '../../../protos/generated/admin.pbsc';
import { AddPlantInverterRequest, GePlantFullDetailByIdRequest, GetAddPlantPrerequisitesResponse, GetCitiesRequest, GetPlantFullDetailByUserIdRequest, GetPlantsRequest, GetPrerequisitesResponse, NewCityRequest, NewPlantRequest, PlantFullDetailResponse, PlantResponse, PlantsResponse, ToggleDisableInverterRequest, ToggleDisablePlantRequest, UpdatePlantInverterRequest, UpdatePlantRequest } from '../../../protos/generated/admin.pb';

@Injectable({
  providedIn: 'root'
})
export class PlantsService {

  constructor(private readonly client: CustomerAssetsEndpointClient, private authService: AuthService) { }
  getMetaData = this.authService.getMetadata();

  /** AddNewPlant */
  addNewPlant(dto: PlantDto): Observable<PlantResponse> {
    const req = new NewPlantRequest({
      plantOwner: dto.plantOwner,
      cityId: dto.cityId,
      userName: dto.username,
      email: dto.email ?? '',
      phoneNumber: dto.phoneNumber ?? '',

      installationDate: dto.installationDate ?? '',

      latitude: dto.latitude ?? 0,
      longitude: dto.longitude ?? 0,
      plantImage: dto.plantImageUrl ?? '',
      address: dto.address ?? '',
      userId: dto.userId ?? 0,
      lowPriceThreshold: dto.lowPriceThreshold ?? '',
      priceClass: dto.priceClass ?? '',
      customerType: dto.customerType ?? '',

      externalReferences: (dto.externalReferences ?? []).map(ref => ({
        serviceName: ref.serviceName,
        serviceValue: ref.serviceValue,
      })),
    });
    return this.client.addNewPlant(
      req,
      this.getMetaData
    );
  }

  /** UpdatePlant */
  updatePlant(
    id: number,
    dto: PlantDto,
    args: {
      cityId: number;
      lowPriceThreshold?: string;
      priceClass?: string;
      customerType?: string;
    },
  ): Observable<PlantResponse> {

    const req = new UpdatePlantRequest({
      id,
      plantOwner: dto.plantOwner,
      cityId: args.cityId,
      email: dto.email ?? '',
      phoneNumber: dto.phoneNumber ?? '',

      installationDate: dto.installationDate ?? '',

      latitude: dto.latitude ?? 0,
      longitude: dto.longitude ?? 0,
      plantImage: dto.plantImageUrl ?? '',
      address: dto.address ?? '',
      lowPriceThreshold: args.lowPriceThreshold ?? '',
      priceClass: args.priceClass ?? '',
      customerType: args.customerType ?? '',

      externalReferences: (dto.externalReferences ?? []).map(ref => ({
        serviceName: ref.serviceName,
        serviceValue: ref.serviceValue,
      })),
    });
    return this.client.updatePlant(req, this.getMetaData);
  }

  getPlants(
    params: { searchText?: string; page?: number; pageSize?: number } = {},
  ): Observable<PlantsResponse> {
    const req = new GetPlantsRequest({
      searchText: params.searchText ?? '',
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 10
    });
    return this.client.getPlants(req, this.getMetaData);
  }

  /** GetPlantFullDetail */
  getPlantFullDetail(id: number): Observable<PlantFullDetailResponse> {
    const req = new GePlantFullDetailByIdRequest({ id });
    const md = this.authService.getMetadata()
    return this.client.getPlantFullDetail(req, md);
  }

  getAddInverterPrerequisites(): Observable<GetPrerequisitesResponse> {
    return this.client
      .getAddInverterPrerequisites(new Empty(), this.getMetaData)
      .pipe(map(res => res));
  }

  getCities(searchText = '') {
    return this.client.getCities(
      new GetCitiesRequest({ searchText }),
      this.getMetaData
    );
  }

  getTimeZones() {
    return this.client.getTimeZones(new Empty(), this.getMetaData);
  }

  addNewCity(
    title: string,
    timeZoneId: string,
    latitude: number,
    longitude: number
  ) {
    return this.client.addNewCity(
      new NewCityRequest({ title, timeZoneId, latitude, longitude }),
      this.getMetaData
    );
  }

  addPlantInverter(data: {
    plantId: number;
    serialNumber: string;
    brand: string;
    model: string;
    maxProductionCapacity: string;
    totalBatteryCapacityKwh: string;
    totalPanelPowerKwh: string;
    batteryChargePowerRateKw: string;
    solarFactorThreshold: string;
    meteringPointIds: { meteringPointId: string; meterType: string; isMain: boolean }[];
  }) {
    const req = new AddPlantInverterRequest({
      plantId: data.plantId,
      serialNumber: data.serialNumber,
      brand: data.brand,
      model: data.model,
      maxProductionCapacity: data.maxProductionCapacity,
      totalBatteryCapacityKwh: data.totalBatteryCapacityKwh,
      totalPanelPowerKwh: data.totalPanelPowerKwh,
      batteryChargePowerRateKw: data.batteryChargePowerRateKw,
      solarFactorThreshold: data.solarFactorThreshold,
      meteringPointIds: (data.meteringPointIds ?? []).map<any>(mp => ({
        meteringPointId: mp.meteringPointId,
        meterType: mp.meterType,
        isMain: mp.isMain,
      })),
    });
    return this.client.addPlantInverter(req, this.getMetaData);
  }

  updatePlantInverter(data: {
    plantId: number;
    id: any;
    serialNumber: string;
    brand: string;
    model: string;
    maxProductionCapacity: string;
    totalBatteryCapacityKwh: string;
    totalPanelPowerKwh: string;
    solarFactorThreshold: any,
    batteryChargePowerRateKw: string;
    meteringPointIds: { meteringPointId: string; meterType: string; isMain: boolean }[];
  }) {
    const req = new UpdatePlantInverterRequest({
      plantId: data.plantId,
      id: data.id,
      serialNumber: data.serialNumber,
      brand: data.brand,
      model: data.model,
      maxProductionCapacity: data.maxProductionCapacity,
      totalBatteryCapacityKwh: data.totalBatteryCapacityKwh,
      totalPanelPowerKwh: data.totalPanelPowerKwh,
      batteryChargePowerRateKw: data.batteryChargePowerRateKw,
      solarFactorThreshold: data.solarFactorThreshold,
      meteringPointIds: (data.meteringPointIds ?? []).map<any>(mp => ({
        meteringPointId: mp.meteringPointId,
        meterType: mp.meterType,
        isMain: mp.isMain,
      })),
    });
    return this.client.updatePlantInverter(req, this.getMetaData);
  }

  getPlantFullDetailByUserId(userId: number) {
    const req = new GetPlantFullDetailByUserIdRequest({ userId });
    const md = this.authService.getMetadata()
    return this.client.getPlantFullDetailByUserId(req, md)
  }

  getInvertersByPlantId(plantId: number): Observable<any[]> {
    return this.getPlantFullDetail(plantId).pipe(
      map((response: any) => response?.inverters ?? [])
    );
  }

  getAddPlantPrerequisites() {
    return this.client
      .getAddPlantPrerequisites(new Empty(), this.getMetaData)
      .pipe(map(res => res as GetAddPlantPrerequisitesResponse));
  }
  toggleDisableInverter(plantId: number, inverterId: number) {
    const req = new ToggleDisableInverterRequest({
      plantId,
      inverterId
    });

    return this.client.toggleDisableInverter(req, this.authService.getMetadata());
  }

  toggleDisablePlant(id: number) {
    const req = new ToggleDisablePlantRequest({ id });
    return this.client.toggleDisablePlant(req, this.authService.getMetadata());
  }

}
